import { useState, useCallback } from "react";

const shortId = () => Math.random().toString(36).slice(2, 8);
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CalendarMonth as CalendarMonthIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import {
  AdvancedFilterItem,
  AdvancedFilterGroup,
  AdvancedFilterModel,
} from "../types";
import {
  getOperatorsForType,
  VALUELESS_OPERATORS,
  ARRAY_VALUE_OPERATORS,
} from "../constants/operatorConfig";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyGroup(): AdvancedFilterGroup {
  return { id: shortId(), logicOperator: "and", items: [] };
}

function emptyItem(): AdvancedFilterItem {
  return { id: shortId(), field: "", operator: "", value: undefined };
}

function defaultModel(): AdvancedFilterModel {
  const group = emptyGroup();
  group.items.push(emptyItem());
  return { groups: [group], groupLogicOperator: "and" };
}

// ─── LogicOperatorToggle ──────────────────────────────────────────────────────

interface LogicToggleProps {
  value: "and" | "or";
  onChange: (v: "and" | "or") => void;
}

function LogicOperatorToggle({ value, onChange }: LogicToggleProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", my: 0.5 }}>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        onChange={(_, v) => v && onChange(v as "and" | "or")}
        sx={{ mx: 1 }}
      >
        <ToggleButton value="and" sx={{ px: 1.5, py: 0.25, fontSize: 11 }}>
          AND
        </ToggleButton>
        <ToggleButton value="or" sx={{ px: 1.5, py: 0.25, fontSize: 11 }}>
          OR
        </ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
    </Box>
  );
}

// ─── ValueInput ───────────────────────────────────────────────────────────────

interface ValueInputProps {
  operator: string;
  value: any;
  columnType?: string;
  onChange: (v: any) => void;
}

function ValueInput({ operator, value, columnType, onChange }: ValueInputProps) {
  // Hook must be at top level — before any conditional returns
  const [inputVal, setInputVal] = useState("");

  if (VALUELESS_OPERATORS.has(operator)) return null;

  if (ARRAY_VALUE_OPERATORS.has(operator)) {
    const chips: string[] = Array.isArray(value) ? value : [];

    const addChip = () => {
      const trimmed = inputVal.trim();
      if (trimmed && !chips.includes(trimmed)) {
        onChange([...chips, trimmed]);
      }
      setInputVal("");
    };

    return (
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
          {chips.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              size="small"
              onDelete={() => onChange(chips.filter((c) => c !== chip))}
            />
          ))}
        </Box>
        <TextField
          size="small"
          placeholder="Add value, press Enter"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addChip();
            }
          }}
          onBlur={addChip}
          onPaste={(e) => {
            const incoming = e.clipboardData
              .getData("text")
              .split(/[\n\t,]+/)
              .map((v) => v.trim())
              .filter(Boolean);
            if (incoming.length <= 1) return;
            e.preventDefault();
            const deduped = incoming.filter((v) => !chips.includes(v));
            if (deduped.length) onChange([...chips, ...deduped]);
            setInputVal("");
          }}
          fullWidth
        />
      </Box>
    );
  }

  if (columnType === "boolean") {
    return (
      <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
        <InputLabel>Value</InputLabel>
        <Select
          label="Value"
          value={value === true ? "true" : value === false ? "false" : ""}
          onChange={(e) => onChange(e.target.value === "true")}
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (columnType === "date" || columnType === "dateTime") {
    const isDateTime = columnType === "dateTime";
    return (
      <TextField
        size="small"
        type={isDateTime ? "datetime-local" : "date"}
        label="Value"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        sx={{ flex: 1, minWidth: isDateTime ? 220 : 170 }}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <CalendarMonthIcon sx={{ fontSize: 18, color: "action.active", pointerEvents: "none" }} />
              </InputAdornment>
            ),
          },
        }}
      />
    );
  }

  return (
    <TextField
      size="small"
      placeholder="Value"
      value={value ?? ""}
      onChange={(e) =>
        onChange(e.target.value === "" ? undefined : e.target.value)
      }
      sx={{ flex: 1, minWidth: 140 }}
    />
  );
}

// ─── FilterRow ────────────────────────────────────────────────────────────────

interface FilterRowProps {
  item: AdvancedFilterItem;
  columns: GridColDef[];
  onChange: (updated: AdvancedFilterItem) => void;
  onRemove: () => void;
}

function FilterRow({ item, columns, onChange, onRemove }: FilterRowProps) {
  const selectedCol = columns.find((c) => c.field === item.field);
  const colType = (selectedCol as any)?.type as string | undefined;
  const operators = getOperatorsForType(colType);

  const handleFieldChange = (field: string) => {
    onChange({ ...item, field, operator: "", value: undefined });
  };

  const handleOperatorChange = (operator: string) => {
    const isValued = !VALUELESS_OPERATORS.has(operator);
    onChange({
      ...item,
      operator,
      value: isValued ? item.value : undefined,
    });
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 0.5 }}>
      {/* Field selector */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Column</InputLabel>
        <Select
          label="Column"
          value={item.field}
          onChange={(e) => handleFieldChange(e.target.value)}
        >
          {columns
            .filter((c) => c.field !== "__check__" && c.filterable !== false)
            .map((c) => (
              <MenuItem key={c.field} value={c.field}>
                {c.headerName ?? c.field}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Operator selector */}
      <FormControl size="small" sx={{ minWidth: 160 }} disabled={!item.field}>
        <InputLabel>Operator</InputLabel>
        <Select
          label="Operator"
          value={item.operator}
          onChange={(e) => handleOperatorChange(e.target.value)}
        >
          {operators.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Value input */}
      {item.operator && (
        <ValueInput
          operator={item.operator}
          value={item.value}
          columnType={colType}
          onChange={(v) => onChange({ ...item, value: v })}
        />
      )}

      {/* Remove row */}
      <Tooltip title="Remove condition">
        <IconButton size="small" onClick={onRemove} sx={{ mt: 0.5 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ─── GroupCard ────────────────────────────────────────────────────────────────

interface GroupCardProps {
  group: AdvancedFilterGroup;
  groupIndex: number;
  totalGroups: number;
  columns: GridColDef[];
  onGroupChange: (updated: AdvancedFilterGroup) => void;
  onGroupRemove: () => void;
}

function GroupCard({
  group,
  groupIndex,
  totalGroups,
  columns,
  onGroupChange,
  onGroupRemove,
}: GroupCardProps) {
  const updateItem = useCallback(
    (itemIndex: number, updated: AdvancedFilterItem) => {
      const newItems = [...group.items];
      newItems[itemIndex] = updated;
      onGroupChange({ ...group, items: newItems });
    },
    [group, onGroupChange]
  );

  const removeItem = useCallback(
    (itemIndex: number) => {
      const newItems = group.items.filter((_, i) => i !== itemIndex);
      onGroupChange({ ...group, items: newItems });
    },
    [group, onGroupChange]
  );

  const addItem = useCallback(() => {
    onGroupChange({ ...group, items: [...group.items, emptyItem()] });
  }, [group, onGroupChange]);

  return (
    <Card variant="outlined" sx={{ p: 1.5, mb: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Group {groupIndex + 1}
        </Typography>
        {totalGroups > 1 && (
          <Tooltip title="Remove group">
            <IconButton size="small" onClick={onGroupRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {group.items.map((item, ii) => (
        <Box key={item.id}>
          {ii > 0 && (
            <LogicOperatorToggle
              value={group.logicOperator}
              onChange={(op) => onGroupChange({ ...group, logicOperator: op })}
            />
          )}
          <FilterRow
            item={item}
            columns={columns}
            onChange={(updated) => updateItem(ii, updated)}
            onRemove={() => removeItem(ii)}
          />
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addItem}
        sx={{ mt: 0.5 }}
      >
        Add Condition
      </Button>
    </Card>
  );
}

// ─── FilterBuilderContent ─────────────────────────────────────────────────────
// Inline version (no Drawer) used inside the CombinedFilterPanel slot.

export interface FilterBuilderContentProps {
  columns: GridColDef[];
  advancedFilterModel: AdvancedFilterModel | null;
  onApply: (model: AdvancedFilterModel) => void;
  onClear: () => void;
  pendingFilterFieldRef?: { current: string | null };
}

export function FilterBuilderContent({
  columns,
  advancedFilterModel,
  onApply,
  onClear,
  pendingFilterFieldRef,
}: FilterBuilderContentProps) {
  // Initialise from prop — component mounts fresh each time the panel opens.
  // If pendingFilterFieldRef is set, consume it and pre-add an item for that field.
  const [localModel, setLocalModel] = useState<AdvancedFilterModel>(() => {
    const base = advancedFilterModel ?? defaultModel();
    const pendingField = pendingFilterFieldRef?.current ?? null;
    if (pendingFilterFieldRef) pendingFilterFieldRef.current = null; // consume once
    if (pendingField) {
      const alreadyPresent = base.groups.some((g) =>
        g.items.some((i) => i.field === pendingField)
      );
      if (!alreadyPresent) {
        const firstGroup = base.groups[0] ?? emptyGroup();
        return {
          ...base,
          groups: [
            { ...firstGroup, items: [...firstGroup.items, { id: shortId(), field: pendingField, operator: "", value: undefined }] },
            ...base.groups.slice(1),
          ],
        };
      }
    }
    return base;
  });

  const updateGroup = useCallback(
    (groupIndex: number, updated: AdvancedFilterGroup) => {
      const newGroups = [...localModel.groups];
      newGroups[groupIndex] = updated;
      setLocalModel({ ...localModel, groups: newGroups });
    },
    [localModel]
  );

  const removeGroup = useCallback(
    (groupIndex: number) => {
      setLocalModel({
        ...localModel,
        groups: localModel.groups.filter((_, i) => i !== groupIndex),
      });
    },
    [localModel]
  );

  const addGroup = useCallback(() => {
    const newGroup = emptyGroup();
    newGroup.items.push(emptyItem());
    setLocalModel({ ...localModel, groups: [...localModel.groups, newGroup] });
  }, [localModel]);

  // Apply is enabled when every group has at least one complete (field + operator) item
  const canApply = localModel.groups.every((g) =>
    g.items.some((item) => item.field && item.operator)
  );

  const handleApply = () => {
    const cleaned: AdvancedFilterModel = {
      groupLogicOperator: localModel.groupLogicOperator,
      groups: localModel.groups
        .map((g) => ({
          ...g,
          items: g.items.filter((i) => i.field && i.operator),
        }))
        .filter((g) => g.items.length > 0),
    };
    if (cleaned.groups.length === 0) {
      onClear();
    } else {
      onApply(cleaned);
    }
  };

  return (
    <Box
      sx={{
        width: 580,
        p: 2,
        display: "flex",
        flexDirection: "column",
        maxHeight: "70vh",
      }}
    >
      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
        {localModel.groups.map((group, gi) => (
          <Box key={group.id}>
            {gi > 0 && (
              <LogicOperatorToggle
                value={localModel.groupLogicOperator}
                onChange={(op) =>
                  setLocalModel({ ...localModel, groupLogicOperator: op })
                }
              />
            )}
            <GroupCard
              group={group}
              groupIndex={gi}
              totalGroups={localModel.groups.length}
              columns={columns}
              onGroupChange={(updated) => updateGroup(gi, updated)}
              onGroupRemove={() => removeGroup(gi)}
            />
          </Box>
        ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addGroup}
          sx={{ mt: 1.5 }}
        >
          Add Group
        </Button>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 2,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button variant="contained" onClick={handleApply} disabled={!canApply}>
          Apply
        </Button>
        <Button variant="outlined" color="error" onClick={onClear}>
          Clear All
        </Button>
      </Box>
    </Box>
  );
}
